/**
 * @fileoverview Plant Search component for searching plants and finding their soil mix
 * @module components/PlantSearch
 * @requires react
 */

import React, { useState, useEffect } from 'react';
import api from '../api';
import './PlantSearch.css';
import { useNavigate } from 'react-router-dom';


/**
 * PlantSearch component for searching plants and finding their soil mix
 * @component
 * @param {Object} props - Component properties
 * @param {Function} props.onSelectSoilMix - Callback function when a soil mix is selected
 * @returns {JSX.Element} PlantSearch component
 */
const PlantSearch = ({ onSelectSoilMix }) => {
  // State for search query
  const [searchQuery, setSearchQuery] = useState('');
  // State for search results
  const [plants, setPlants] = useState([]);
  // State for loading status
  const [loading, setLoading] = useState(false);
  // State for error message
  const [error, setError] = useState(null);
  // State for debounce timer
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Example plant suggestions
  const suggestions = ['Basil', 'Mint', 'Parsley'];

  // Get the navigate function
  const navigate = useNavigate();

  /**
   * Handle search input change
   * @param {Event} e - Change event
   */
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set new timer to avoid too many API calls
    const timer = setTimeout(() => {
      if (query.trim().length > 0) {
        searchPlants(query);
      } else {
        setPlants([]);
      }
    }, 500);

    setDebounceTimer(timer);
  };

  /**
   * Handle suggestion click
   * @param {string} suggestion - Suggestion text
   */
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    searchPlants(suggestion);
  };

  /**
   * Search plants from API
   * @param {string} query - Search query
   */
  const searchPlants = async (query) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/api/plants/search?query=${encodeURIComponent(query)}`);
      const plantsData = response.data.plants;
      setPlants(plantsData);
  
    } catch (err) {
      console.error('Error searching plants:', err);
      setError('Error searching plants. Please try again.');
    } finally {
      setLoading(false);
    }
  };

/**
 * Handle plant selection
 * @param {Object} plant - Selected plant
 */
const handlePlantSelect = (plant) => {
  console.log('Plant selected:', plant);
  // Navigate directly to the plant's product page
  navigate(`/products/${plant._id}`);
};

  // Clean up timer on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return (
    <div className="plant-search-container">
      <h2 className="plant-search-title">Find the Perfect Soil Mix for Your Plant</h2>
      
      <div className="search-box">
        <label htmlFor="plant-search" className="search-label">Search</label>
        <input
          id="plant-search"
          type="text"
          className="search-input"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Enter plant name..."
        />
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading-message">
          {plants.length > 0 ? 'Finding the perfect soil mix for your plant...' : 'Searching plants...'}
        </div>
      ) : (
        <>
          {searchQuery.trim() === '' ? (
            <div className="welcome-message">
              <h3>Welcome to SoiledIt!</h3>
              <p>We provide custom soil mixes specifically formulated for your plants.</p>
              <p>Simply start typing the name of your plant above to see our recommended soil mix.</p>
              
              <div className="search-suggestions">
                <h4>Try searching for:</h4>
                <ul>
                  {suggestions.map((suggestion, index) => (
                    <li key={index}>
                      <button 
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : plants.length > 0 ? (
            <div className="plant-results">
              <h3>Search Results:</h3>
              {plants.map((plant) => (
                <div
                  key={plant._id}
                  className="plant-card"
                  onClick={() => handlePlantSelect(plant)}
                >
                  <div className="plant-name">{plant.name}</div>
                  {plant.scientificName && (
                    <div className="plant-scientific">{plant.scientificName}</div>
                  )}
                </div>
              ))}
            </div>
          ) : searchQuery.trim() !== '' && !loading ? (
            <div className="no-results">No plants found. Try a different search term.</div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default PlantSearch;